import { $generateHtmlFromNodes } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useCallback, useEffect, useRef } from 'react';

interface Props {
  onChange: (text: string) => void;
}

// Function to normalize HTML by removing nested formatting tags
export const normalizeHTML = (html: string): string => {
  // Create a temporary DOM element to parse and normalize HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Function to recursively normalize nested formatting tags
  const normalizeElement = (element: Element): void => {
    const children = Array.from(element.children);

    for (const child of children) {
      const tagName = child.tagName.toLowerCase();

      // Check if this is a formatting tag
      if (
        ['b', 'strong', 'i', 'em', 'u', 's', 'del', 'strike'].includes(tagName)
      ) {
        // Check if parent is also a formatting tag of the same type
        const parentTagName = element.tagName.toLowerCase();

        // Remove nested tags when they are semantically equivalent
        if (
          ['b', 'strong'].includes(tagName) &&
          ['b', 'strong'].includes(parentTagName)
        ) {
          // Remove nested bold tags (b->strong, strong->b, b->b, strong->strong)
          const textContent = child.textContent || '';
          child.replaceWith(document.createTextNode(textContent));
        } else if (
          ['i', 'em'].includes(tagName) &&
          ['i', 'em'].includes(parentTagName)
        ) {
          // Remove nested italic tags (i->em, em->i, i->i, em->em)
          const textContent = child.textContent || '';
          child.replaceWith(document.createTextNode(textContent));
        } else if (['u'].includes(tagName) && ['u'].includes(parentTagName)) {
          // Remove nested underline tags
          const textContent = child.textContent || '';
          child.replaceWith(document.createTextNode(textContent));
        } else if (
          ['s', 'del', 'strike'].includes(tagName) &&
          ['s', 'del', 'strike'].includes(parentTagName)
        ) {
          // Remove nested strikethrough tags
          const textContent = child.textContent || '';
          child.replaceWith(document.createTextNode(textContent));
        }
      }

      // Recursively normalize children
      normalizeElement(child);
    }
  };

  // Normalize all elements
  normalizeElement(tempDiv);

  return tempDiv.innerHTML;
};

function OnChangeHTMLPlugin({ onChange }: Props) {
  const [editor] = useLexicalComposerContext();
  const isInitializingRef = useRef(true);
  const skipNextUpdateRef = useRef(false);
  const lastHtmlRef = useRef<string>('');

  // Memoized function to handle editor updates
  const handleEditorUpdate = useCallback(
    ({ editorState, dirtyElements, dirtyLeaves, tags }: any) => {
      // Skip updates that are triggered by InitialValuePlugin
      if (skipNextUpdateRef.current) {
        skipNextUpdateRef.current = false;
        return;
      }

      // Skip the very first update (initialization) to avoid triggering onChange
      if (isInitializingRef.current) {
        isInitializingRef.current = false;
        return;
      }

      // Skip updates that are only about selection changes (no content changes)
      if (tags.has('history-merge') || tags.has('history-push')) {
        return;
      }

      // Skip if no actual content changes (only selection/focus changes)
      if (dirtyElements.size === 0 && dirtyLeaves.size === 0) {
        return;
      }

      editor.update(() => {
        const htmlString = $generateHtmlFromNodes(editor);

        // Normalize HTML to remove nested formatting tags
        const normalizedHtml = normalizeHTML(htmlString);

        // Only trigger onChange if HTML actually changed
        if (normalizedHtml !== lastHtmlRef.current) {
          lastHtmlRef.current = normalizedHtml;
          onChange(normalizedHtml);
        }
      });
    },
    [editor, onChange],
  );

  useEffect(() => {
    const unsubscribe = editor.registerUpdateListener(handleEditorUpdate);
    return unsubscribe;
  }, [editor, handleEditorUpdate]);

  // Expose method to skip next update (for InitialValuePlugin to use)
  (editor as any).__skipNextUpdate = () => {
    skipNextUpdateRef.current = true;
  };

  return null;
}

export default OnChangeHTMLPlugin;
